/**
 * **Leads Cockpit** - the invitations and submissions of the customer onboarding (platform root only).
 *
 * A list with status chips and a filter, "Neue Einladung" for a new invitation (the link is shown
 * once, in the dialog), and a detail view per lead. The public side of the flow is
 * `src/onboarding/OnboardingPage.tsx`; the contract is tri-server `docs/onboarding-leads.md`.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ButtonTip, TipProvider } from "@/components/ui/button-tip";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ADMIN_FORBIDDEN_TEXT, onboardingAdminAPI, type AdminLeadSummary, type LeadStatus } from "../OnboardingAPI";
import { useCockpitErrors } from "./leads/cockpitErrors";
import DeleteLeadDialog from "./leads/DeleteLeadDialog";
import InviteDialog from "./leads/InviteDialog";
import LeadDetailSheet from "./leads/LeadDetailSheet";
import { EXISTING_USER_NOTICE } from "./leads/InviteDialog";
import { dataMark } from "./leads/TenantDataBlock";
import { PROVISIONING_LABEL, PROVISIONING_VARIANT, STATUS_LABEL, STATUS_ORDER, STATUS_VARIANT, assetClassLabel, formatDate, formatDateTime, relativeTime, stepLabel } from "./leads/leadLabels";

export default function LeadsCockpitPanel() {
  // errors are shown as the server's sentence, not as "ClassName: text"
  const { showError: pushError } = useCockpitErrors();
  const [leads, setLeads] = useState<AdminLeadSummary[] | null>(null);
  const [filter, setFilter] = useState<LeadStatus | "all">("all");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<AdminLeadSummary | null>(null);
  // The routes are for the platform root only; anyone else gets a 403 and a plain note, no error dialog.
  const [forbidden, setForbidden] = useState(false);

  const reload = useCallback(async () => {
    try {
      setLeads(await onboardingAdminAPI.list());
    } catch (error) {
      if ((error as { status?: number })?.status === 403) setForbidden(true);
      else pushError(error);
      setLeads((current) => current ?? []);
    }
  }, [pushError]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const counts = useMemo(() => {
    const result = new Map<LeadStatus, number>();
    (leads ?? []).forEach((lead) => result.set(lead.status, (result.get(lead.status) ?? 0) + 1));
    return result;
  }, [leads]);

  const visible = useMemo(
    () => (leads ?? []).filter((lead) => filter === "all" || lead.status === filter),
    [leads, filter],
  );

  const chip = (value: LeadStatus | "all", label: string, count: number) => (
    <ButtonTip key={value} label={value === "all" ? "Zeigt alle Leads" : `Zeigt nur Leads im Status „${label}“`}>
      <button
        type="button"
        aria-pressed={filter === value}
        onClick={() => setFilter(value)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          filter === value ? "border-primary bg-primary text-primary-foreground" : "border-input hover:bg-accent",
        )}
      >
        {label}
        <span className={cn("tabular-nums text-xs", filter === value ? "opacity-80" : "text-muted-foreground")}>{count}</span>
      </button>
    </ButtonTip>
  );

  if (forbidden) {
    return (
      <div className="p-4" data-testid="leads-cockpit-panel">
        <Alert variant="warning">
          <AlertDescription>{ADMIN_FORBIDDEN_TEXT}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <TipProvider>
    <div className="flex min-w-0 flex-col gap-4 p-4" data-testid="leads-cockpit-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl text-sm text-muted-foreground">
          Interessenten bekommen einen persönlichen Link, tragen dort ihr Firmenprofil ein und laden ihre Daten hoch. Neu eingegangene Leads
          stehen hervorgehoben in der Liste; nach dem Absenden legt das System den Zugang an und schickt die Willkommensmail. Du prüfst Dateien und Analyse und startest danach den Import.
        </div>
        <div className="flex gap-2">
          <ButtonTip label="Lädt die Liste neu" side="bottom">
            <Button variant="outline" onClick={() => void reload()} aria-label="Liste aktualisieren">
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
            </Button>
          </ButtonTip>
          <ButtonTip label="Legt einen Lead mit persönlichem Link an" side="bottom">
            <Button onClick={() => setInviteOpen(true)}>
              <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> Neue Einladung
            </Button>
          </ButtonTip>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Nach Status filtern">
        {chip("all", "Alle", leads?.length ?? 0)}
        {STATUS_ORDER.map((status) => chip(status, STATUS_LABEL[status], counts.get(status) ?? 0))}
      </div>

      {!leads ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Firma</TableHead>
                <TableHead>Kontakt</TableHead>
                <TableHead>Assetklasse</TableHead>
                <TableHead className="text-right">Dateien</TableHead>
                <TableHead>Eingang</TableHead>
                <TableHead>Ablauf</TableHead>
                <TableHead className="text-right">Geöffnet</TableHead>
                <TableHead>Weit gekommen bis</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Mandant</TableHead>
                <TableHead className="w-12 text-right"><span className="sr-only">Aktionen</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((lead) => (
                <TableRow
                  key={lead.id}
                  className={cn("cursor-pointer", lead.status === "submitted" && "bg-warning/10")}
                  onClick={() => setOpenId(lead.id)}
                  data-testid={`lead-row-${lead.id}`}
                >
                  <TableCell className="font-medium">
                    <ButtonTip label="Öffnet die Detailansicht" side="right">
                      <button
                        type="button"
                        className="text-left underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={(event) => {
                          event.stopPropagation();
                          setOpenId(lead.id);
                        }}
                      >
                        {lead.companyName}
                      </button>
                    </ButtonTip>
                  </TableCell>
                  <TableCell className="text-xs">
                    {lead.contactName?.trim() && <div>{lead.contactName}</div>}
                    <div className="text-muted-foreground">{lead.contactEmail}</div>
                    {lead.phoneVerified === true && (
                      <Badge variant="success" className="mt-1" title="Die Handynummer wurde mit einem Code bestätigt." data-testid={`phone-verified-${lead.id}`}>
                        Telefon bestätigt
                      </Badge>
                    )}
                    {lead.contactConflict === true && (
                      <Badge variant="warning" className="mt-1" title={EXISTING_USER_NOTICE} data-testid={`contact-conflict-${lead.id}`}>
                        Zugang existiert
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{assetClassLabel(lead.assetClass)}</TableCell>
                  <TableCell className="text-right tabular-nums">{lead.fileCount}</TableCell>
                  <TableCell className="text-xs tabular-nums">{formatDateTime(lead.submittedAt)}</TableCell>
                  <TableCell className="text-xs tabular-nums">{formatDate(lead.tokenExpiresAt)}</TableCell>
                  <TableCell className="text-right text-xs tabular-nums" title={lead.lastOpenedAt ? `zuletzt ${formatDateTime(lead.lastOpenedAt)}` : undefined} data-testid={`opens-${lead.id}`}>
                    <div>{lead.openCount === undefined ? "-" : lead.openCount}</div>
                    {lead.lastOpenedAt && <div className="text-muted-foreground">zuletzt {relativeTime(lead.lastOpenedAt)}</div>}
                  </TableCell>
                  <TableCell className="text-xs" data-testid={`furthest-${lead.id}`}>{stepLabel(lead.furthestStep)}</TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[lead.status]}>{STATUS_LABEL[lead.status]}</Badge>
                  </TableCell>
                  <TableCell data-testid={`tenant-${lead.id}`}>
                    {lead.provisioning ? (
                      <Badge variant={PROVISIONING_VARIANT[lead.provisioning.state]}>{PROVISIONING_LABEL[lead.provisioning.state]}</Badge>
                    ) : (
                      "-"
                    )}
                    {/* what became of the lead's data in the tenant; nothing from an older server */}
                    {dataMark(lead.adoptionState, lead.importState) && (
                      <div className="mt-1 text-xs text-muted-foreground" data-testid={`data-${lead.id}`}>
                        Daten: {dataMark(lead.adoptionState, lead.importState)}
                        {lead.importState === "not_started" && <span data-testid={`import-open-${lead.id}`}> · Import offen</span>}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <ButtonTip label="Lead löschen" side="left">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      aria-label={`Lead ${lead.companyName} löschen`}
                      data-testid={`delete-row-${lead.id}`}
                      onClick={(event) => {
                        // not the row: the detail view must not open
                        event.stopPropagation();
                        setDeleting(lead);
                      }}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    </ButtonTip>
                  </TableCell>
                </TableRow>
              ))}
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} className="text-sm text-muted-foreground">
                    {leads.length === 0 ? "Noch keine Einladung." : "Kein Lead mit diesem Status."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <DeleteLeadDialog
        open={!!deleting}
        leadId={deleting?.id ?? null}
        companyName={deleting?.companyName ?? ""}
        customerId={deleting?.provisioning?.state === "done" ? deleting.provisioning.customerId : undefined}
        adoptionState={deleting?.adoptionState}
        status={deleting?.status}
        fileCount={deleting?.fileCount}
        onClose={() => setDeleting(null)}
        onDeleted={() => {
          setDeleting(null);
          toast.success("Lead gelöscht");
          void reload();
        }}
        onError={pushError}
      />
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} onCreated={() => void reload()} onError={pushError} />
      <LeadDetailSheet leadId={openId} onClose={() => setOpenId(null)} onChanged={() => void reload()} onError={pushError} />
    </div>
    </TipProvider>
  );
}
